/* eslint-disable no-empty-pattern */
import { MarkerType } from 'reactflow';

import { MODULE_NAME } from './const';
import { getNode } from './getters';
import { SET_NODE_DATA, SET_LOADING_TEST_AND_CONTINUE, DELETE_NODE, BULK_SET_NODES_DATA } from './mutations';
import state from './state';
import StoreModuleService from '../StoreModuleService';
import { getAction } from '../actions/getters';
import { useEdge } from '../edges/getters';
import { editNode } from '../selectedNode/getters';
import { closeSideBarIfEditNodeDeleted } from '../selectedWorkflow/actions';
import { getWidgetData, bulkFetchWidgetsData } from '../widgets/actions';
import { addNodeToWorkflowNodes, syncWorkflowVersionNodes } from '../workflows/actions';
import api from '@/api';
import FlowTreeService from '@/components/FlowTree/flow-tree-service';
import { EdgeTypes } from '@/components/FlowTree/flow-tree-service.const';
import { handleGraphEdgesAffectedChanges } from '@/store/modules/edges/actions';
import { selectedWorkflowId } from '@/store/modules/selectedWorkflow/getters';
import {
	generalConfigurationId as generalConfigurationIdGetter,
	userSpecificConfigurationId as userSpecificConfigurationIdGetter,
} from '@/store/modules/selectedWorkflow/modules/configurations/getters';
import { removeTempBlock } from '@/store/modules/treeBlocks/actions';
import { getConfiguration as getConfigurationAction } from '@/store/modules/workflowConfigurations/actions';
import AppActionService from '@/utils/AppActionService';
import { arrayToObject, isNullOrUndefined } from '@/utils/helpers';
import * as WorkflowNodeService from '@/utils/workflow-node-service';

const buildNodeActionObject = (action) => StoreModuleService.buildActionObject(MODULE_NAME, action);

// actions declarations
export const exampleAction = buildNodeActionObject('exampleAction');
export const setNodeData = buildNodeActionObject('setNodeData');
export const bulkSetNodesData = buildNodeActionObject('bulkSetNodesData');
export const getNodeData = buildNodeActionObject('getNodeData');
export const getNodesByIds = buildNodeActionObject('getNodesByIds');
export const bulkGetNodesByIds = buildNodeActionObject('bulkGetNodesByIds');
export const fetchNodeData = buildNodeActionObject('fetchNodeData');
export const updateNodeData = buildNodeActionObject('updateNodeData');
export const updateNode = buildNodeActionObject('updateNode');
export const deleteNode = buildNodeActionObject('deleteNode');
export const toggleNodeType = buildNodeActionObject('toggleNodeType');
export const testStep = buildNodeActionObject('testStep');
export const skipTest = buildNodeActionObject('skipTest');
export const handleNodeUpdated = buildNodeActionObject('handleNodeUpdated');
export const handleNodesUpdated = buildNodeActionObject('handleNodesUpdated');
export const deleteNodeData = buildNodeActionObject('deleteNodeData');
export const handleNodeDeleted = buildNodeActionObject('handleNodeDeleted');
export const partialUpdateNode = buildNodeActionObject('partialUpdateNode');
export const duplicateStep = buildNodeActionObject('duplicateStep');
export const updateNodeTestResult = buildNodeActionObject('updateNodeTestResult');
export const updateAffectedNodes = buildNodeActionObject('updateAffectedNodes');
export const resetWidgetFieldTestStatus = buildNodeActionObject('resetWidgetFieldTestStatus');
export const fetchNodeSideEffectsData = buildNodeActionObject('fetchNodeSideEffectsData');
export const fetchNodesSideEffectsDataByIds = buildNodeActionObject('fetchNodesSideEffectsDataByIds');
export const createStep = buildNodeActionObject('createStep');
export const handleGraphAffectedChanges = buildNodeActionObject('handleGraphAffectedChanges');
export const handleGraphNodesAffectedChanges = buildNodeActionObject('handleGraphNodesAffectedChanges');
export const addBranchPath = buildNodeActionObject('addBranchPath');
export const deleteBranchPath = buildNodeActionObject('deleteBranchPath');
export const updateBranchPath = buildNodeActionObject('updateBranchPath');
export const addBranchVariable = buildNodeActionObject('addBranchVariable');
export const deleteBranchVariable = buildNodeActionObject('deleteBranchVariable');
export const updateBranchVariable = buildNodeActionObject('updateBranchVariable');
export const updateBranchVariableMapping = buildNodeActionObject('updateBranchVariableMapping');
export const fetchConfigurationsIfNodeCredentialsConfigurationChanged = buildNodeActionObject(
	'fetchConfigurationsIfNodeCredentialsConfigurationChanged'
);

const actions = {
	...StoreModuleService.createDefaultActions(),
	async [getNodeData.action]({ dispatch }, payload) {
		const { id, published, forceGet } = payload;
		const dataIsMissing = isNullOrUndefined(state.data[id]);
		const needToGet = dataIsMissing || forceGet;
		if (needToGet) {
			await dispatch(fetchNodeData.action, { id, published });
		}
	},
	async [handleGraphAffectedChanges.action]({ dispatch }, { changes }) {
		const { nodes, edges } = changes;
		const createdNodes = nodes.created;
		const createdLoopNodes = createdNodes.filter(WorkflowNodeService.isLoop);
		const tempEdges = { ...edges };
		if (createdLoopNodes.length > 0) {
			const sortedLoopNodes = createdLoopNodes.toSorted((a, b) => a.id - b.id);
			const allLoopStarts = [];
			const allLoopEnds = [];
			sortedLoopNodes.forEach((node, i) => {
				const isLoopStart = i % 2 === 0;
				if (isLoopStart) {
					allLoopStarts.push(node);
				} else {
					allLoopEnds.push(node);
				}
			});
			const loopConnectorEdges = allLoopStarts.map((loopStart, i) => {
				const loopEnd = allLoopEnds[i];
				return {
					target: loopEnd.id,
					source: loopStart.id,
					type: EdgeTypes.LoopConnector,
					id: `${loopStart.id}=>${loopEnd.id}`,
					workflow_version: loopStart.workflow_version,
					markerStart: {
						type: MarkerType.ArrowClosed,
						width: 20,
						height: 20,
						// raises `react-dom.development.js:798 Error: <marker> attribute orient: Expected angle, "rotate(90deg)".` on render, but works 🤷‍♂️
						orient: 'rotate(90deg)',
					},
				};
			});
			tempEdges.created = [...tempEdges.created, ...loopConnectorEdges];
		}
		// todo-graph: remove this when BE is ready. this is a hack to render loops for now
		return Promise.all([
			dispatch(handleGraphNodesAffectedChanges.action, { nodes }),
			dispatch(handleGraphEdgesAffectedChanges.fullPath, { edges: tempEdges }, { root: true }),
		]);
	},
	async [handleGraphNodesAffectedChanges.action]({ dispatch, rootGetters }, { nodes }) {
		const { created, updated, deleted } = nodes;
		const addNodesToWorkflowNodePromise = created.map((node) =>
			dispatch(addNodeToWorkflowNodes.fullPath, { id: rootGetters[selectedWorkflowId.fullPath], nodeId: node.id }, { root: true })
		);
		return Promise.all([
			dispatch(updateAffectedNodes.action, { nodes: [...updated, ...created] }),
			addNodesToWorkflowNodePromise,
			// this delete can be improved to be in a single commit, for less re-renders
			dispatch(updateAffectedNodes.action, { nodes: deleted.map((nodeId) => ({ id: nodeId, deleted: true })) }),
		]);
	},
	async [addBranchPath.action]({ dispatch }, { branchRootId }) {
		const changes = await api.addBranchPath(branchRootId);
		return dispatch(handleGraphAffectedChanges.action, { changes });
	},
	async [deleteBranchPath.action]({ dispatch }, { branchRootId, branchPathId }) {
		const changes = await api.deleteBranchPath(branchRootId, branchPathId);
		return dispatch(handleGraphAffectedChanges.action, { changes });
	},
	async [updateBranchPath.action]({ dispatch }, { branchRootId, branchPathId, partialObjectToUpdate }) {
		const changes = await api.updateBranchPath(branchRootId, branchPathId, partialObjectToUpdate);
		return dispatch(handleGraphAffectedChanges.action, { changes });
	},
	async [addBranchVariable.action]({ dispatch }, { branchRootId }) {
		const changes = await api.addBranchVariable(branchRootId);
		return dispatch(handleGraphAffectedChanges.action, { changes });
	},
	async [deleteBranchVariable.action]({ dispatch }, { branchRootId, branchVariableId }) {
		const changes = await api.deleteBranchVariable(branchRootId, branchVariableId);
		return dispatch(handleGraphAffectedChanges.action, { changes });
	},
	async [updateBranchVariable.action]({ dispatch }, { branchRootId, branchVariableId, partialObjectToUpdate }) {
		const changes = await api.updateBranchVariable(branchRootId, branchVariableId, partialObjectToUpdate);
		return dispatch(handleGraphAffectedChanges.action, { changes });
	},
	async [updateBranchVariableMapping.action]({ dispatch }, { branchRootId, branchPathId, branchId, partialObjectToUpdate }) {
		const changes = await api.updateBranchVariableMapping(branchRootId, branchPathId, branchId, partialObjectToUpdate);
		return dispatch(handleGraphAffectedChanges.action, { changes });
	},

	[setNodeData.action]({ commit }, payload) {
		const { data: nodeData } = payload;
		commit(SET_NODE_DATA, { key: nodeData.id, value: nodeData });
	},
	[bulkSetNodesData.action]({ commit }, payload) {
		const { data: nodesData } = payload;
		commit(BULK_SET_NODES_DATA, { data: arrayToObject(nodesData) });
	},
	async [getNodesByIds.action]({ dispatch }, { nodeIds = [], forceGet = false, published, workflowVersionId }) {
		const nodeIdsToGet = nodeIds.filter((nodeId) => isNullOrUndefined(state.data[nodeId]) || forceGet);
		const nodes = nodeIdsToGet?.length > 0 ? await api.getNodesByIds(nodeIdsToGet) : [];
		await Promise.all([
			dispatch(bulkSetNodesData.action, { data: nodes }),
			...nodes.map(async (node) =>
				dispatch(fetchNodeSideEffectsData.action, {
					node,
					published,
					workflowVersionId,
					nodeBeforeUpdate: state.data[node.id],
				})
			),
		]);

		return nodes;
	},

	async [bulkGetNodesByIds.action]({ dispatch }, { nodeIds = [], forceGet = false, workflowVersionId }) {
		const nodeIdsToGet = nodeIds.filter((nodeId) => isNullOrUndefined(state.data[nodeId]) || forceGet);
		const nodes = nodeIdsToGet?.length > 0 ? await api.getNodesByIds(nodeIdsToGet) : [];

		await dispatch(fetchNodesSideEffectsDataByIds.action, { nodes, workflowVersionId });
		await dispatch(bulkSetNodesData.action, { data: nodes });

		return nodes;
	},
	async [resetWidgetFieldTestStatus.action]({ dispatch }, { nodeId, widgetFieldId }) {
		const node = await api.resetWidgetFieldTestStatus(nodeId, widgetFieldId);
		await dispatch(setNodeData.action, { data: node });
	},
	async [fetchNodeData.action]({ dispatch }, payload) {
		const { id, published } = payload;
		const ret = await api.getNode(id);
		await dispatch(updateNodeData.action, { data: ret, published, forceGet: true });
	},
	async [updateNodeData.action]({ dispatch, getters }, payload) {
		const { data, published } = payload;
		const nodeBeforeUpdate = getters[getNode.getter](data.id);
		await dispatch(setNodeData.action, { data });
		const { workflow: workflowId, workflow_version: workflowVersionId } = data;
		await dispatch(syncWorkflowVersionNodes.fullPath, { workflowId, workflowVersionId, nodeId: data.id }, { root: true });
		await dispatch(fetchNodeSideEffectsData.action, {
			published,
			node: data,
			nodeBeforeUpdate,
			workflowVersionId,
		});
	},
	async [fetchNodeSideEffectsData.action]({ dispatch, rootGetters }, { node, published, workflowVersionId, nodeBeforeUpdate }) {
		if (node.params_map && node.params_map.widget_id) {
			await dispatch(
				getWidgetData.fullPath,
				{ id: node.params_map.widget_id, workflowVersionId, forceGet: true, published },
				{ root: true }
			);
		}
		if (node.credentials_configuration?.source_field_uuid) {
			// In case the node credentials configuration changed, we need to fetch the configurations again, so we can render the freshly created credentials selector field
			const generalConfigurationId = rootGetters[generalConfigurationIdGetter.fullPath];
			const userSpecificConfigurationId = rootGetters[userSpecificConfigurationIdGetter.fullPath];
			await dispatch(fetchConfigurationsIfNodeCredentialsConfigurationChanged.action, {
				nodeBeforeUpdate,
				nodeAfterUpdate: node,
				generalConfigurationId,
				userSpecificConfigurationId,
			});
		}
	},
	async [fetchNodesSideEffectsDataByIds.action]({ dispatch, rootGetters }, { nodes, workflowVersionId }) {
		const widgetIds = nodes.map((n) => n?.params_map?.widget_id).filter(Boolean);
		if (widgetIds.length > 0) {
			await dispatch(bulkFetchWidgetsData.fullPath, { widgetIds, workflowVersionId, forceGet: true }, { root: true });
		}

		for (const node of nodes) {
			if (node.credentials_configuration?.source_field_uuid) {
				// In case the node credentials configuration changed, we need to fetch the configurations again, so we can render the freshly created credentials selector field
				const generalConfigurationId = rootGetters[generalConfigurationIdGetter.fullPath];
				const userSpecificConfigurationId = rootGetters[userSpecificConfigurationIdGetter.fullPath];
				await dispatch(fetchConfigurationsIfNodeCredentialsConfigurationChanged.action, {
					nodeBeforeUpdate: state.data[node.id],
					nodeAfterUpdate: node,
					generalConfigurationId,
					userSpecificConfigurationId,
				});
			}
		}
	},
	async [fetchConfigurationsIfNodeCredentialsConfigurationChanged.action](
		{ dispatch },
		{ nodeBeforeUpdate, nodeAfterUpdate, generalConfigurationId, userSpecificConfigurationId }
	) {
		const previousCredentialsConfigurationSourceFieldUUID = nodeBeforeUpdate?.credentials_configuration.source_field_uuid;
		const newCredentialsConfigurationSourceFieldUUID = nodeAfterUpdate.credentials_configuration.source_field_uuid;
		const didCredentialsConfigurationChange =
			previousCredentialsConfigurationSourceFieldUUID !== newCredentialsConfigurationSourceFieldUUID;
		if ((newCredentialsConfigurationSourceFieldUUID && didCredentialsConfigurationChange) || !nodeBeforeUpdate) {
			if (generalConfigurationId) {
				await dispatch(getConfigurationAction.fullPath, { id: generalConfigurationId, forceGet: true }, { root: true });
			}
			if (userSpecificConfigurationId) {
				await dispatch(getConfigurationAction.fullPath, { id: userSpecificConfigurationId, forceGet: true }, { root: true });
			}
		}
	},
	async [updateNode.action]({ dispatch, rootGetters }, payload) {
		const { node, partial } = payload;
		if (!node || !node.id) return;
		const isUpdatingTempNode = WorkflowNodeService.isNodeTemp(node);
		if (isUpdatingTempNode) {
			return dispatch(createStep.action, { node: { edge: rootGetters[editNode.fullPath].edge, ...node } });
		} else {
			const { affected_nodes: affectedNodes, ...ret } = await api.updateNode(node, partial);
			if (affectedNodes) {
				await dispatch(updateAffectedNodes.action, { nodes: affectedNodes.filter((affectedNode) => affectedNode.id !== node.id) });
			}
			await dispatch(updateNodeData.action, { data: ret });
			return ret;
		}
	},
	async [createStep.action]({ dispatch, rootGetters }, { node }) {
		const edge = rootGetters[useEdge.fullPath](Number(node.edge));
		const isServerEdge = FlowTreeService.isServerEdge(edge?.type);
		if (!isServerEdge) {
			node.edge = null;
		}
		const nodeKeysToSendInApi = ['edge', 'service', 'action', 'action_slug', 'fallbacks', 'node_title', 'params_map'];
		const relevantNode = nodeKeysToSendInApi.reduce((acc, key) => {
			acc[key] = node[key];
			return acc;
		}, {});
		const changes = await api.createStep(rootGetters[selectedWorkflowId.fullPath], relevantNode);
		await Promise.all([dispatch(handleGraphAffectedChanges.action, { changes }), dispatch(removeTempBlock.fullPath, {}, { root: true })]);
		return changes;
	},
	async [partialUpdateNode.action]({ dispatch, getters }, { id, data }) {
		const currentNodeState = getters[getNode.getter](id);
		const node = { ...currentNodeState, ...data };
		return dispatch(updateNode.action, { node });
	},
	async [deleteNode.action]({ dispatch }, { workflowId, nodeId }) {
		if (WorkflowNodeService.isNodeTemp({ id: nodeId })) {
			// This code should be unreachable
			return;
		}
		try {
			const changes = await api.deleteStep(workflowId, nodeId);
			await dispatch(handleGraphAffectedChanges.action, { changes });
		} catch (err) {
			return { error: err };
		}
	},
	[toggleNodeType.action]({}, payload) {
		const { id: nodeId } = payload;
		return api.toggleNodeType(nodeId);
	},
	async [updateNodeTestResult.action]({ dispatch, state }, payload) {
		const { id: nodeId, testResponse } = payload;
		let currentNodeData = state.data[nodeId];
		currentNodeData = { ...currentNodeData, ...testResponse };
		await dispatch(updateNodeData.action, { data: currentNodeData });
	},
	async [testStep.action]({ commit, getters, dispatch, rootGetters }, payload) {
		const { id } = payload;
		var ret;
		commit(SET_LOADING_TEST_AND_CONTINUE, true); // TODO - loadingFlag for each nodeId
		try {
			const currentNode = getters[getNode.getter](id);
			const isWidget = WorkflowNodeService.isWidget(currentNode);
			const action = rootGetters[getAction.fullPath](currentNode.action);
			const isActionOfEndRunWidget = AppActionService.isActionOfEndRunWidget(action);
			if (isWidget || isActionOfEndRunWidget) {
				const { widgetData } = payload;
				const { values, labels } = widgetData;
				ret = await api.saveWidgetTest(id, { values }, labels);
			} else {
				delete payload.id;
				ret = await api.testStep(id, payload);
			}
		} catch (error) {
			console.error(error);
			ret = {
				error: true,
			};
		}
		commit(SET_LOADING_TEST_AND_CONTINUE, false);
		await dispatch(updateNodeTestResult.action, { id, testResponse: ret });
		return ret;
	},
	async [skipTest.action]({ dispatch }, { id }) {
		const skipResponse = await api.skipTestStep(id);
		await dispatch(partialUpdateNode.action, { id, data: skipResponse });
		return skipResponse;
	},
	[handleNodesUpdated.action]({ dispatch }, payload) {
		const { nodeIds } = payload;
		return dispatch(getNodesByIds.action, { nodeIds, forceGet: true });
	},
	[handleNodeUpdated.action]({ dispatch }, payload) {
		const { pk: nodeId } = payload;
		return dispatch(getNodeData.action, { id: nodeId, forceGet: true });
	},
	[handleNodeDeleted.action]({ dispatch }, payload) {
		const { pk: nodeId } = payload;
		return dispatch(deleteNodeData.action, { id: nodeId });
	},
	async [deleteNodeData.action]({ commit, dispatch, getters }, payload) {
		const { id: nodeId } = payload;
		const nodeToDelete = getters[getNode.getter](nodeId);
		if (nodeToDelete) {
			const { workflow: workflowId, workflow_version: workflowVersionId } = nodeToDelete;
			await dispatch(syncWorkflowVersionNodes.fullPath, { workflowId, workflowVersionId, nodeId, remove: true }, { root: true });
			commit(DELETE_NODE, nodeId);
			await dispatch(closeSideBarIfEditNodeDeleted.fullPath, { id: nodeId }, { root: true });
		}
	},
	async [duplicateStep.action]({ dispatch }, { nodeId, workflowId }) {
		const changes = await api.duplicateStep(workflowId, nodeId);
		await dispatch(handleGraphAffectedChanges.action, { changes });
	},
	[updateAffectedNodes.action]({ dispatch }, payload) {
		const { nodes } = payload;
		return Promise.all(
			nodes.map((node) =>
				node.deleted ? dispatch(deleteNodeData.action, { id: node.id }) : dispatch(updateNodeData.action, { data: node, forceGet: true })
			)
		);
	},
};

export default actions;
